import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import { ExcelFile, ExcelSheet, ExcelColumn } from 'react-xlsx-wrapper';
import CommonHeader from '../../../util_components/common_header';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import CustomInput from '../../../util_components/custom_input';
import CustomDatePicker from '../../../util_components/custom_date_picker';
import { post_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import {
  day_format,
  time_format,
  nutritionProgramPaymentsDownloadList,
  nutritionProgramPaymentStatusOptions
} from '../../../../constants';
import * as actions from '../../../../redux/action_creators';

const PAGE_SIZE = 100; // matches the endpoint default; also its per-page cap driver
// Export pulls the full filtered set in one shot (offset 0), not just the visible page.
// The apis report endpoint may cap limit server-side; if so the export silently truncates.
const EXPORT_LIMIT = 10000;

// Escape CSV/Excel formula-injection: a cell starting with = + - @ is prefixed with a quote so
// spreadsheet apps treat it as text, never as a formula. Applied to every exported cell value.
const escape_report_cell = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /^[=+\-@]/.test(s) ? `'${s}` : s;
};

// Derived-status badge colors, mapped to theme tokens (no hardcoded hex).
const status_style = (status) => {
  const map = {
    PAID: { bg: 'var(--state-success-bg)', color: 'var(--state-success-text)' },
    FAILED: { bg: 'var(--state-error-bg)', color: 'var(--state-error-text)' },
    REFUNDED: { bg: 'var(--state-warning-bg)', color: 'var(--state-warning-text)' },
    PENDING: { bg: 'var(--state-info-bg)', color: 'var(--state-info-text)' }
  };
  const c = map[status] || { bg: 'var(--surface-card)', color: 'var(--text-muted)' };
  return {
    background: c.bg,
    color: c.color,
    padding: '2px 8px',
    borderRadius: '10px',
    display: 'inline-block',
    fontWeight: 600
  };
};

class NutritionProgramPayments extends Component {
  state = {
    loading: true,
    rows: [],
    total_count: 0,
    // Default to the last 30 days so the report opens with recent activity, not empty.
    date_from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    date_to: moment().format('YYYY-MM-DD'),
    status: '',
    currency: '',
    offset: 0,
    // Download exports the full filtered set, fetched separately from the paged `rows`.
    download_rows: [],
    downloading: false
  };

  // Wraps react-xlsx-wrapper's ExcelFile; we click its inner element once the full data lands.
  excel_ref = React.createRef();

  componentDidMount() {
    this.load_data();
  }

  load_data = () => {
    this.setState({ loading: true }, () => {
      const { date_from, date_to, status, currency, offset } = this.state;
      const payload = { limit: PAGE_SIZE, offset };
      // All filters optional — omit empties so the backend treats them as "all".
      if (date_from) payload.date_from = date_from;
      if (date_to) payload.date_to = date_to;
      if (status) payload.status = status;
      if (currency) payload.currency = currency.trim().toUpperCase();

      const url = get_api_url('nutrition_program_payments');
      // Report envelope is TOP-LEVEL {status, rows, count, total_count} (no data wrapper).
      post_api(url, payload, true)
        .then((res) => {
          const body = (res && res.data) || {};
          this.setState({
            loading: false,
            rows: body.rows || [],
            total_count: body.total_count || 0
          });
        })
        .catch((e) => {
          let err_message;
          try {
            err_message = e.response.data.reason || e.response.data.message;
          } catch (err) {
            err_message = 'Failed to load nutrition program payments. Please contact dev team.';
          }
          this.props.set_notification_variable(true, 'error', err_message);
          this.setState({ loading: false, rows: [], total_count: 0 });
        });
    });
  };

  // Filter changes reset paging to the first page; date/currency need an explicit Apply.
  handle_date_change = (name, val) => this.setState({ [name]: val });

  change_status = (e) => this.setState({ status: e.target.value, offset: 0 }, this.load_data);

  change_currency = (e) => this.setState({ currency: e.target.value });

  apply_filters = () => this.setState({ offset: 0 }, this.load_data);

  prev_page = () => {
    const { offset } = this.state;
    if (offset <= 0) return;
    this.setState({ offset: Math.max(0, offset - PAGE_SIZE) }, this.load_data);
  };

  next_page = () => {
    const { offset, total_count } = this.state;
    if (offset + PAGE_SIZE >= total_count) return;
    this.setState({ offset: offset + PAGE_SIZE }, this.load_data);
  };

  // amount/refund/net render as integers (display-unit) — no forced decimals (breaks JPY/KRW etc.).
  fmt_money = (val, currency) => {
    const n = Number(val);
    const safe = Number.isFinite(n) ? n : 0;
    return currency ? `${currency} ${safe}` : String(safe);
  };

  // Fetch the FULL filtered result set (not the paged view) then trigger the hidden
  // ExcelFile once the data is in state. Keeps the on-screen paging (100/page) intact.
  download_full = () => {
    if (this.state.downloading) return;
    this.setState({ downloading: true }, () => {
      const { date_from, date_to, status, currency } = this.state;
      const payload = { limit: EXPORT_LIMIT, offset: 0 };
      if (date_from) payload.date_from = date_from;
      if (date_to) payload.date_to = date_to;
      if (status) payload.status = status;
      if (currency) payload.currency = currency.trim().toUpperCase();

      const url = get_api_url('nutrition_program_payments');
      post_api(url, payload, true)
        .then((res) => {
          const body = (res && res.data) || {};
          const download_rows = body.rows || [];
          if (body.total_count && download_rows.length < body.total_count) {
            // Guard against a silent truncation if the set ever exceeds EXPORT_LIMIT.
            this.props.set_notification_variable(
              true,
              'warning',
              `Exporting the first ${download_rows.length} of ${body.total_count} rows. Narrow the filters to export the rest.`
            );
          }
          // Set state first so the hidden ExcelFile re-renders with the full data, then click it.
          this.setState({ downloading: false, download_rows }, () => {
            const node = this.excel_ref.current;
            const btn = node && node.querySelector ? node.querySelector('button') : null;
            if (btn) btn.click();
          });
        })
        .catch((e) => {
          let err_message;
          try {
            err_message = e.response.data.reason || e.response.data.message;
          } catch (err) {
            err_message = 'Failed to download nutrition program payments. Please try again.';
          }
          this.props.set_notification_variable(true, 'error', err_message);
          this.setState({ downloading: false });
        });
    });
  };

  // Flatten the full-set download_rows into the export shape; escape every cell against
  // formula injection. Only used for the Excel export (the on-screen table uses render_row).
  get_excel_data = () => {
    const { download_rows } = this.state;
    const tz = this.props.iana_timezone;
    return download_rows.map((r) => ({
      student_name: escape_report_cell(r.student_name),
      student_email: escape_report_cell(r.student_email),
      status: escape_report_cell(r.status),
      amount: escape_report_cell(r.amount),
      currency: escape_report_cell(r.currency),
      refund_amount: escape_report_cell(r.refund_amount),
      net: escape_report_cell(r.net),
      refund_reason: escape_report_cell(r.refund_reason),
      invoice_id: escape_report_cell(r.invoice_id),
      charge_id: escape_report_cell(r.charge_id),
      enrolled_date: escape_report_cell(
        r.enrolled_date ? moment(r.enrolled_date).tz(tz).format('YYYY-MM-DD HH:mm:ss z') : ''
      ),
      utm_source: escape_report_cell(r.utm_source)
    }));
  };

  render_filters = () => {
    const { date_from, date_to, status, currency } = this.state;
    return (
      <div className="flex flex-wrap items-end gap-3 mb-3 justify-center">
        <div className="daily-report-item">
          <CustomDatePicker
            label="From Date"
            value={date_from}
            handleChange={(val) => this.handle_date_change('date_from', val)}
            maxDate={date_to}
          />
        </div>
        <div className="daily-report-item">
          <CustomDatePicker
            label="To Date"
            value={date_to}
            handleChange={(val) => this.handle_date_change('date_to', val)}
            minDate={date_from}
            maxDate={moment().format('YYYY-MM-DD')}
          />
        </div>
        <div style={{ width: '180px' }}>
          {/* Status options include an explicit "All" (empty value); suppress the widget's own
              blank default option by reusing the "All" label so there's no duplicate blank row. */}
          <CustomSingleSelect
            label="Status"
            defaultOption="All"
            options={nutritionProgramPaymentStatusOptions.filter((o) => o.value !== '')}
            onChange={this.change_status}
            value={status}
          />
        </div>
        <div style={{ width: '160px' }}>
          <CustomInput
            label="Currency (e.g. USD)"
            value={currency}
            onChange={this.change_currency}
          />
        </div>
        <button type="button" className="ct-small-btn" onClick={this.apply_filters}>
          Apply
        </button>
        {/* Download fetches the FULL filtered set (all pages), not just the visible page. */}
        <button
          type="button"
          className="data-download-btn m-0"
          onClick={this.download_full}
          disabled={this.state.downloading}
          style={{ opacity: this.state.downloading ? 0.5 : 1 }}
        >
          {this.state.downloading ? 'Preparing…' : 'Download Data'}
        </button>
        {/* Hidden ExcelFile bound to the full download_rows; download_full() clicks it once
            the fetch lands. Kept mounted so it re-renders with the fetched data before click. */}
        <span ref={this.excel_ref} style={{ display: 'none' }}>
          <ExcelFile
            element={<button type="button">export</button>}
            filename={`nutrition_program_payments_${moment().format('YYYY-MM-DD')}_${uuid()}`}
          >
            <ExcelSheet data={this.get_excel_data()} name="Nutrition Program Payments">
              {nutritionProgramPaymentsDownloadList.map((d) => (
                <ExcelColumn key={d.value} label={d.label} value={d.value} />
              ))}
            </ExcelSheet>
          </ExcelFile>
        </span>
      </div>
    );
  };

  render_row = (r, idx) => {
    const tz = this.props.iana_timezone;
    return (
      <div className="ct-row" key={`${r.charge_id || r.invoice_id || 'row'}-${idx}`}>
        <div className="ct-col ct-hf ct-la ct-bw">
          {r.student_uuid ? (
            <a
              className="link-no-dec"
              href={`/studentprofile?id=${r.student_uuid}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div>{r.student_name || '--'}</div>
              <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
                {r.student_email || ''}
              </div>
            </a>
          ) : (
            <div>
              <div>{r.student_name || '--'}</div>
              <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
                {r.student_email || ''}
              </div>
            </div>
          )}
        </div>
        <div className="ct-col ct-qf ct-vsm-font">
          <span style={status_style(r.status)}>{r.status || '--'}</span>
        </div>
        <div className="ct-col ct-qf ct-sm-font">{this.fmt_money(r.amount, r.currency)}</div>
        <div className="ct-col ct-qf ct-sm-font">
          {this.fmt_money(r.refund_amount, r.currency)}
        </div>
        <div className="ct-col ct-qf ct-sm-font">{this.fmt_money(r.net, r.currency)}</div>
        <div className="ct-col ct-qf ct-vsm-font ct-bw">{r.refund_reason || '--'}</div>
        <div className="ct-col ct-qf ct-vsm-font ct-bw">
          <div>{r.invoice_id || '--'}</div>
          <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
            {r.charge_id || ''}
          </div>
        </div>
        <div className="ct-col ct-qf ct-vsm-font">{r.utm_source || '--'}</div>
        <div className="ct-col ct-qf ct-sm-font">
          {r.enrolled_date ? (
            <>
              <div>{moment(r.enrolled_date).tz(tz).format(day_format)}</div>
              <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
                {moment(r.enrolled_date).tz(tz).format(time_format)}
              </div>
            </>
          ) : (
            '--'
          )}
        </div>
      </div>
    );
  };

  render() {
    const { loading, rows, total_count, offset } = this.state;
    const page_start = total_count === 0 ? 0 : offset + 1;
    const page_end = Math.min(offset + rows.length, total_count);
    const has_prev = offset > 0;
    const has_next = offset + PAGE_SIZE < total_count;
    return (
      <CommonHeader loading={loading} title="Nutrition Program Payments">
        {/* One row per charge attempt — failed/pending/refunded attempts all appear. */}
        <div className="ct-vsm-font mb-2" style={{ color: 'var(--text-muted)' }}>
          Grain: one row per charge attempt (a student who failed then paid shows multiple rows).
        </div>
        {this.render_filters()}
        <div className="ct-vsm-font mb-1" style={{ color: 'var(--text-muted)' }}>
          {total_count === 0
            ? 'No results'
            : `Showing ${page_start}–${page_end} of ${total_count}`}
        </div>
        <div className="ct-table" style={{ minWidth: '1100px' }}>
          <div className="ct-row ct-header-row">
            <div className="ct-col ct-hf ct-la">Student</div>
            <div className="ct-col ct-qf">Status</div>
            <div className="ct-col ct-qf">Amount</div>
            <div className="ct-col ct-qf">Refund</div>
            <div className="ct-col ct-qf">Net</div>
            <div className="ct-col ct-qf">Refund Reason</div>
            <div className="ct-col ct-qf">Invoice / Charge</div>
            <div className="ct-col ct-qf">UTM Source</div>
            <div className="ct-col ct-qf">Enrolled</div>
          </div>
          {rows.length === 0 && !loading ? (
            <div className="ct-row">
              <div className="ct-col" style={{ color: 'var(--text-muted)' }}>
                No nutrition program payments for this filter.
              </div>
            </div>
          ) : (
            rows.map(this.render_row)
          )}
        </div>
        {total_count > PAGE_SIZE ? (
          <div className="flex items-center gap-3 justify-center mt-3">
            <button
              type="button"
              className="ct-small-btn"
              onClick={this.prev_page}
              disabled={!has_prev}
              style={{ opacity: has_prev ? 1 : 0.5 }}
            >
              Prev
            </button>
            <span className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
              {page_start}–{page_end} / {total_count}
            </span>
            <button
              type="button"
              className="ct-small-btn"
              onClick={this.next_page}
              disabled={!has_next}
              style={{ opacity: has_next ? 1 : 0.5 }}
            >
              Next
            </button>
          </div>
        ) : null}
      </CommonHeader>
    );
  }
}

const mapStateToProps = (state) => ({
  iana_timezone: state.home.iana_timezone
});

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(NutritionProgramPayments);
